import * as THREE from 'three';
import { REGIONS } from '../pharmacology/regions';
import { resolveAnchor } from './loadCortexMesh';

/**
 * Canvas-texture text labels for each anatomical region, positioned just
 * above their region node. Hidden by default; revealed all at once by
 * Learn Mode.
 */
export class LabelSprites {
  readonly group: THREE.Group;
  private readonly sprites: THREE.Sprite[] = [];

  constructor() {
    this.group = new THREE.Group();

    for (const region of Object.values(REGIONS)) {
      const sprite = createLabelSprite(region.displayName);
      const [x, y, z] = resolveAnchor(region.anchor);

      // Deep structures cluster tightly around the brain's centre, so their
      // labels stack illegibly if drawn in place. Push each label outward along
      // its own radial direction, by an amount that shrinks the further out the
      // structure already is: the central cluster fans apart, while cortical
      // labels stay close to the region they name. labelOffset then breaks
      // remaining vertical ties.
      const distance = Math.sqrt(x * x + y * y + z * z);
      const push = distance < 1e-4 ? 0 : Math.max(0.1, 0.62 - Math.min(0.5, distance)) / distance;

      sprite.position.set(
        x * (1 + push),
        y * (1 + push) + 0.14 + (region.labelOffset ?? 0),
        z * (1 + push),
      );
      sprite.scale.set(0.55, 0.14, 1);
      this.group.add(sprite);
      this.sprites.push(sprite);
    }
  }

  setVisible(visible: boolean): void {
    for (const sprite of this.sprites) {
      (sprite.material as THREE.SpriteMaterial).opacity = visible ? 0.9 : 0;
    }
  }
}

function createLabelSprite(text: string): THREE.Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 28px Inter, system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 4, 32);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    opacity: 0,
    depthTest: false,
  });

  return new THREE.Sprite(material);
}
